const QueryParams = require('../../../page/frontend/helpers/params');
const urlUpdate = require('../../../page/frontend/helpers/url_update');
const DealFetcher = require('./helpers/deal_fetcher');
const PaginationRenderer = require('./helpers/pagination_renderer');
const Spinner = require('./helpers/spinner');
const scrollPositionHelper = require('../../../page/frontend/helpers/scroll_position');
const carouselScrollPositionsHelper = require('../../../page/frontend/helpers/carousel_scroll_positions');
const { windowLoaded } = require('@grpn/document-states');
const { itemExists } = require('./helpers/local_storage');
const taxonomyPreferences = require('./helpers/taxonomy_preferences');
const { bucketAAExperiment } = require('../../../page/frontend/helpers/finch');

const context = {
  initialize() {
    context.loadApp();
  },
  loadApp() {
    context.setDefaults();
    context.initializeObjects();
    context.attachListeners();
    context.initializeAAExperiment();
    windowLoaded.then(() => {
      context.onLoad();
    });
  },
  setDefaults() {
    context.urlParams = Groupon.Utils.unparam();
  },
  onLoad() {
    context.lazyLoadAssets();
    context.restorePreviousState();
  },
  initializeObjects() {
    context.domConfig = Groupon.Utils.getConfigFromDom();
    context.spinner = new Spinner();
    context.dealFetcher = new DealFetcher(context.domConfig.csrfToken);
    context.paginationRenderer = new PaginationRenderer();
    context.intersectionObserver = new IntersectionObserver(
      this.onScrollFetchLazyLoadedCardsHandler,
      {
        root: null,
        rootMargin: '0px 0px 600px 0px',
        threshold: 0.01,
      }
    );
  },
  attachListeners() {
    window.addEventListener('popstate', context.handleOnPopState);
  },
  bucketAAExperiment(entries) {
    if (entries.some(entry => entry.isIntersecting)) {
      bucketAAExperiment('20-01_AA1_WebHomepage');
      context.aaIntersectionObserver.disconnect();
    }
  },
  initializeAAExperiment() {
    context.aaIntersectionObserver = new IntersectionObserver(
      context.bucketAAExperiment
    );
    const threeUpCollection = document.querySelector('#small-featured');
    if (threeUpCollection) {
      context.aaIntersectionObserver.observe(threeUpCollection);
    }
  },
  handleOnPopState() {
    const newUrlParams = Groupon.Utils.unparam();
    if (context.urlParams.page === newUrlParams.page) {
      return;
    }

    const page = context.getPageNumberFromUrl(window.location.href);

    context.fetchPaginatedCards(page);
  },
  lazyLoadAssets() {
    window.Groupon.Utils.loadAsyncJS(
      context.domConfig.homeDesktopLazyLoadedCardUiJavascript,
      context.onLazyLoadedCardUiJavascript
    );
    if (
      context.domConfig.showTaxonomyPreferences &&
      !itemExists('shownTaxonomyPreferences')
    ) {
      window.Groupon.Utils.loadAsyncJS(
        context.domConfig.homeDesktopTaxonomyLazyLoadedJavascript,
        context.onLazyLoadedTaxonomy
      );
      context.lazyLoadCss(context.domConfig.homeDesktopTaxonomyLazyLoadedCss);
    }
  },

  onLazyLoadedCardUiJavascript() {
    window.CardUI.init();
  },
  onLazyLoadedTaxonomy() {
    const showDialog = function (selectedPreferences) {
      window.taxonomyDialogLoader.showTaxonomyDialog.call(
        context,
        GIG.Modal,
        window.taxonomyDialogLoader.onUserClickedTaxonomyTile,
        selectedPreferences
      );
    };

    try {
      taxonomyPreferences
        .fetch({ _csrf: context.domConfig.csrfToken })
        .then(selectedPreferences => showDialog(selectedPreferences));
    } catch (e) {
      showDialog();
    }
  },
  lazyLoadCss(href) {
    const link = document.createElement('link');
    link.css = 'text/javascript';
    link.rel = 'stylesheet';
    link.href = href;
    document.getElementsByTagName('head')[0].appendChild(link);
  },
  loadAds(tryAmount = 0) {
    if (Groupon.DFPAds) {
      Groupon.DFPAds.load();
    } else {
      if (tryAmount < 20) {
        setTimeout(() => {
          context.loadAds(++tryAmount);
        }, 100);
      }
    }
  },
  onPaginationClick(event) {
    event.preventDefault();

    const page = context.getPageNumberFromUrl(event.currentTarget.href);

    const paramObject = Groupon.Utils.unparam();
    paramObject.page = page;

    const updatedUrl = `${window.location.pathname}?${QueryParams.toString(
      paramObject
    )}`;
    urlUpdate.update(updatedUrl, { pushState: true });
    context.fetchPaginatedCards(page);
  },
  getPageNumberFromUrl(url) {
    let page = 1;

    const queryParams = url.split('?')[1];

    if (queryParams && queryParams.length) {
      const queryParamHash = Groupon.Utils.unparam(queryParams);

      if (queryParamHash.page) {
        page = queryParamHash.page;
      }
    }

    return page;
  },
  getLazyLoadedSectionIndex() {
    context.lazyLoadedSectionIndex = !context.lazyLoadedSectionIndex
      ? 1
      : ++context.lazyLoadedSectionIndex;

    return context.lazyLoadedSectionIndex;
  },
  attachOnScrollFetchLazyLoadedCardsListener() {
    const cardShells = document.querySelector('#card-shells');
    if (cardShells) {
      context.intersectionObserver.observe(cardShells);
    }
  },
  onScrollFetchLazyLoadedCardsHandler() {
    context.fetchLazyLoadedCards();
    context.intersectionObserver.disconnect();
  },
  fetchLazyLoadedCards() {
    const cardShells = document.querySelector('#card-shells');
    if (!cardShells) {
      return;
    }

    context.dealFetcher
      .fetchLazyLoadedCards(context.getLazyLoadedSectionIndex())
      .then(context.handleLazyLoadedCardsFetchedSuccess)
      .catch(context.handleLazyLoadedCardsFetchedError);
  },
  fetchPaginatedCards(page) {
    const cardShells = document.querySelector('#card-shells');
    if (cardShells) {
      return;
    }

    context.spinner.start();

    context.dealFetcher
      .fetchPaginatedCards(page)
      .then(context.handlePaginatedCardsFetchedSuccess)
      .catch(context.handlePaginatedCardsFetchedError)
      .then(context.spinner.stop.bind(context.spinner));
  },
  handleLazyLoadedCardsFetchedSuccess(response) {
    if (
      !response ||
      !response.cardSlotsHtml ||
      !response.cardSlotsHtml.length
    ) {
      return;
    }

    const scrollPos =
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;
    context.paginationRenderer.clearCardShells();
    context.paginationRenderer.insertCards(response.cardSlotsHtml);
    window.scrollTo(0, scrollPos);

    context.paginationRenderer.attachPaginationClickHandler(
      context.onPaginationClick
    );

    context.attachCardClickHandlers();
    context.loadAds();
    context.attachOnScrollFetchLazyLoadedCardsListener();
    context.attachModalHandlers();
  },
  /* eslint no-empty: "error" */
  handleLazyLoadedCardsFetchedError() {},
  handlePaginatedCardsFetchedSuccess(response) {
    if (
      !response ||
      !response.cardSlotsHtml ||
      !response.cardSlotsHtml.length
    ) {
      return;
    }

    context.paginationRenderer.clearPaginatedCards();
    context.paginationRenderer.insertCards(response.cardSlotsHtml);
    context.paginationRenderer.scrollToTop();
    context.paginationRenderer.attachPaginationClickHandler(
      context.onPaginationClick
    );

    context.attachCardClickHandlers();
    context.urlParams = Groupon.Utils.unparam();
  },
  /* eslint no-empty: "error" */
  handlePaginatedCardsFetchedError() {},
  attachCardClickHandlers() {
    const allCards = document.querySelectorAll(
      '.card-ui, .location-rememberance'
    );

    if (allCards && allCards.length) {
      allCards.forEach(card => {
        card.addEventListener('click', context.onCardCLicked);
      });
    }
  },

  attachModalHandlers() {
    if (GIG && GIG.Modal) {
      GIG.Modal.init();
    }
  },

  onCardCLicked() {
    const lazyLoadedCardsHtml = document.querySelectorAll(
      'div[data-is-lazy-loaded="true"]'
    );

    context.setCardsAndScrollPositionInBrowserHistory(
      context.sanitizeCardsHtml(lazyLoadedCardsHtml),
      carouselScrollPositionsHelper.getCarouselScrollPositions(),
      scrollPositionHelper.getScrollPosition()
    );
  },
  setCardsAndScrollPositionInBrowserHistory(
    lazyLoadedCardsHtml,
    carouselScrollPositions,
    scrollPosition
  ) {
    const page = context.getPageNumberFromUrl(window.location.href);

    const state = {
      lazyLoadedCardsHtml,
      lazyLoadedSectionIndex: context.lazyLoadedSectionIndex,
      carouselScrollPositions,
      scrollPosition,
      page,
    };

    history.replaceState(state, `Page ${page}`, window.location.href);
  },
  sanitizeCardsHtml(cardsHtml) {
    const combinedHtml = [];
    if (cardsHtml && cardsHtml.length) {
      cardsHtml.forEach(node => {
        let htmlString = node.outerHTML;

        if (node.classList.contains('pull-dfp')) {
          if (node.children.length)
            htmlString = htmlString.replace(node.children[0].innerHTML, '');
        }

        combinedHtml.push(htmlString);
      });
    }
    return combinedHtml.join('');
  },
  restorePreviousState() {
    if (
      history.state &&
      history.state.lazyLoadedCardsHtml &&
      history.state.lazyLoadedSectionIndex &&
      history.state.carouselScrollPositions &&
      history.state.scrollPosition &&
      history.state.page === context.getPageNumberFromUrl(window.location.href)
    ) {
      context.lazyLoadedSectionIndex = history.state.lazyLoadedSectionIndex;

      context.handleLazyLoadedCardsFetchedSuccess({
        cardSlotsHtml: history.state.lazyLoadedCardsHtml,
      });

      carouselScrollPositionsHelper.restoreCarouselScrollPositions(
        history.state.carouselScrollPositions
      );

      window.scrollTo(0, history.state.scrollPosition);
    } else {
      context.fetchLazyLoadedCards();
    }

    context.setCardsAndScrollPositionInBrowserHistory(undefined, 0);
  },
};

export default context;
